import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { categoriesData } from '../../Home/categoriesData';
import { SnackbarProvider } from 'notistack';
import SuccessionThemeProvider from '../SuccessionThemeProvider';
import SuccessionDrawerWithListener from '../../../containers/Succession/SuccessionDrawerWithListener';
import { SingleColumnTemplate, ToolsGridTemplate, HeroBannerTemplate } from '../../../../../ui/layouts';
import GrowthExitDecisionCard from '../GrowthExitDecisionCard';
import GuidedTour from '../../Shared/GuidedTour/GuidedTour';
import ValueAccelerationSprintContainer from '../../../containers/ValueAccelerationSprintContainer';
import { Zap, Settings, TrendingUp, CheckCircle2, TrendingDown, ArrowRight, ClipboardList, FileText } from 'lucide-react';
import ReadinessScoreHero from './ReadinessScoreHero';
import YourGapsCompact from './YourGapsCompact';
import Button from '../../../../../ui/canon/Button';

// Empty State Component for users who haven't completed the PlanBuilder
const PlanBuilderEmptyState = ({ planProgress }) => {
  const intl = useIntl();
  // Consider "in progress" if status is in_progress/paused, or if any progress has been made
  const hasStarted = planProgress?.status === 'in_progress' ||
                     planProgress?.status === 'paused' ||
                     (planProgress?.completion_percentage && planProgress.completion_percentage > 0);
  const buttonText = hasStarted
    ? intl.formatMessage({ id: 'succession.value_acceleration.category_page.finish_editing_plan' })
    : intl.formatMessage({ id: 'succession.value_acceleration.category_page.start_succession_plan' });

  return (
    <div className="flex flex-col items-center py-6 px-4">
      <div className="bg-succession-bg-secondary rounded-2xl border border-succession-border p-8 md:p-10 max-w-2xl text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-teal-100 dark:bg-teal-900/30 mb-6">
          <ClipboardList className="w-10 h-10 text-teal-600 dark:text-teal-400" />
        </div>

        <h2 className="text-2xl md:text-3xl font-bold text-succession-text-primary mb-4">
          {intl.formatMessage({ id: 'succession.value_acceleration.category_page.empty_plan_title' })}
        </h2>

        <p className="text-succession-text-secondary text-lg mb-6 leading-relaxed">
          {intl.formatMessage({ id: 'succession.value_acceleration.category_page.empty_plan_body' })}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 text-left">
          <div className="flex items-start gap-3 p-4 rounded-lg bg-succession-bg-primary">
            <FileText className="w-5 h-5 text-teal-600 dark:text-teal-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-succession-text-primary text-sm">{intl.formatMessage({ id: 'succession.value_acceleration.category_page.empty_plan_readiness_score_label' })}</p>
              <p className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.value_acceleration.category_page.empty_plan_readiness_score_detail' })}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 rounded-lg bg-succession-bg-primary">
            <Zap className="w-5 h-5 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-succession-text-primary text-sm">{intl.formatMessage({ id: 'succession.value_acceleration.category_page.empty_plan_gap_analysis_label' })}</p>
              <p className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.value_acceleration.category_page.empty_plan_gap_analysis_detail' })}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 rounded-lg bg-succession-bg-primary">
            <ArrowRight className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-succession-text-primary text-sm">{intl.formatMessage({ id: 'succession.value_acceleration.category_page.empty_plan_action_sprints_label' })}</p>
              <p className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.value_acceleration.category_page.empty_plan_action_sprints_detail' })}</p>
            </div>
          </div>
        </div>

        <Button
          size="lg"
          className="bg-teal-600 hover:bg-teal-700 text-white px-8"
          onClick={() => window.location.href = '/succession/plans/comprehensive-succession-plan-v2'}
        >
          {buttonText}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>

        <p className="text-sm text-succession-text-secondary mt-4">
          {intl.formatMessage({ id: 'succession.value_acceleration.category_page.already_started' })}
        </p>
      </div>
    </div>
  );
};

const ValueAccelerationCategoryPage = (props) => {
  const intl = useIntl();
  const name = (props?.current_user && props.current_user.name) || (typeof document !== 'undefined' && document.store?.getState?.()?.user?.name) || 'there';
  // Merge user from props and store - props take precedence for diagnostic fields (fresh from server)
  const storeUser = (() => { try { return (typeof document !== 'undefined' && document.store?.getState?.()?.user) || {}; } catch(_) { return {}; } })();
  // Props override store for diagnostic fields to ensure fresh server data is used
  const user = { ...storeUser, ...(props.current_user || {}) };

  // DEBUG: Log what we're receiving
  console.log('[ValueAccelerationCategoryPage] DEBUG props.current_user:', props.current_user);
  console.log('[ValueAccelerationCategoryPage] DEBUG storeUser keys:', Object.keys(storeUser));
  console.log('[ValueAccelerationCategoryPage] DEBUG merged user.diag_completed_at:', user.diag_completed_at);
  console.log('[ValueAccelerationCategoryPage] DEBUG merged user.diag_readiness_total:', user.diag_readiness_total);
  // Get company from store (includes features)
  const storeCompany = (() => { try { return (typeof document !== 'undefined' && document.store?.getState?.()?.company) || {}; } catch(_) { return {}; } })();
  const company = { ...(props.company || {}), ...storeCompany };
  const [hideDecision, setHideDecision] = useState(false);
  const hasCompletedAssessment = (() => {
    const explicit = user['has_completed_triggering_events?'];
    if (explicit !== undefined && explicit !== null) return Boolean(explicit);
    if (user.has_completed_triggering_events !== undefined) return Boolean(user.has_completed_triggering_events);
    const evts = Array.isArray(user.triggering_events) ? user.triggering_events : [];
    return evts.some((e) => e && e.completed_at);
  })();

  // Check if user has completed the PlanBuilder - use state to handle URL params after mount
  // Initialize to null to show loading state while we check
  const [hasCompletedPlanBuilder, setHasCompletedPlanBuilder] = useState(() => {
    // Initial check from user data (available immediately)
    const planProgress = user.plan_builder_progress || user.user_plan_progress;
    if (planProgress && planProgress.status === 'completed') return true;
    if (user.diag_completed_at) return true;
    if (user.diag_readiness_total || user.diag_attractiveness_total) return true;
    // Note: Removed hasCompletedAssessment legacy fallback - PlanBuilder is now required
    // Check URL params immediately if window is available
    if (typeof window !== 'undefined') {
      try {
        const params = new URLSearchParams(window.location.search || '');
        if (params.get('from') === 'plan_builder') return true;
      } catch (_) {}
    }
    return false;
  });

  // Re-check URL params after mount (belt and suspenders)
  useEffect(() => {
    // Debug logging
    console.log('[ValueAccelerationCategoryPage] Checking completion status...');
    console.log('[ValueAccelerationCategoryPage] user.diag_completed_at:', user.diag_completed_at);
    console.log('[ValueAccelerationCategoryPage] user.diag_readiness_total:', user.diag_readiness_total);
    console.log('[ValueAccelerationCategoryPage] user.diag_attractiveness_total:', user.diag_attractiveness_total);
    console.log('[ValueAccelerationCategoryPage] props.current_user:', props.current_user);

    let completed = false;

    // Check URL params for direct navigation from plan completion
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search || '');
      const fromPlanBuilder = params.get('from') === 'plan_builder';
      console.log('[ValueAccelerationCategoryPage] URL from=plan_builder:', fromPlanBuilder);
      if (fromPlanBuilder) {
        completed = true;
      }
    }

    // Check from props or store for plan progress
    const planProgress = user.plan_builder_progress || user.user_plan_progress;
    if (planProgress && planProgress.status === 'completed') {
      console.log('[ValueAccelerationCategoryPage] Plan progress completed');
      completed = true;
    }

    // Check if diag_completed_at is set (set by PlanBuilderActionPlanService)
    if (user.diag_completed_at) {
      console.log('[ValueAccelerationCategoryPage] diag_completed_at is set');
      completed = true;
    }

    // Check for diag totals (set by PlanBuilderActionPlanService)
    if (user.diag_readiness_total || user.diag_attractiveness_total) {
      console.log('[ValueAccelerationCategoryPage] diag totals are set');
      completed = true;
    }

    // Note: Removed hasCompletedAssessment legacy fallback - PlanBuilder is now required

    console.log('[ValueAccelerationCategoryPage] Final hasCompletedPlanBuilder:', completed);
    setHasCompletedPlanBuilder(completed);
  }, [user, props.current_user]);

  const shouldShowDecision = Boolean(
    hasCompletedAssessment &&
    !user.permanently_snoozed_growth_exit_prompt &&
    !(user.growth_exit_snoozed_until && new Date(user.growth_exit_snoozed_until) > new Date()) &&
    !user.exit_decision_at
  ) && !hideDecision;

  // Check if marketplace_sprints feature is enabled (at user or company level)
  const hasMarketplaceSprints =
    (Array.isArray(user?.features) && user.features.includes('marketplace_sprints')) ||
    (Array.isArray(company?.features) && company.features.includes('marketplace_sprints')) ||
    (Array.isArray(company?.feature_types) && company.feature_types.includes('marketplace_sprints')) ||
    (Array.isArray(props?.features) && props.features.includes('marketplace_sprints')) ||
    false;
  // Derive tools for the growth category (used in sidebar tools grid)
  const cat = (categoriesData || []).find((c) => c.id === 'growth');
  const tools = (cat && cat.tools) || [];

  // Direct URL check as fallback (in case state didn't initialize correctly)
  const urlBypassCheck = (() => {
    if (typeof window === 'undefined') return false;
    try {
      const params = new URLSearchParams(window.location.search || '');
      return params.get('from') === 'plan_builder';
    } catch (_) {
      return false;
    }
  })();

  // Value Acceleration Tour Gate - auto-show on first visit when not in empty state
  const [tourOpen, setTourOpen] = useState(false);
  const [tourSteps, setTourSteps] = useState(null);

  // Tour steps definition
  const VALUE_ACCELERATION_TOUR_STEPS = [
    { id: 'readiness-score', title: intl.formatMessage({ id: 'succession.value_acceleration.category_page.tour_readiness_score_title' }), description: intl.formatMessage({ id: 'succession.value_acceleration.category_page.tour_readiness_score_desc' }), targetSelector: '[data-tutorial-id="readiness-score"]', position: 'right' },
    { id: 'your-gaps', title: intl.formatMessage({ id: 'succession.value_acceleration.category_page.tour_your_gaps_title' }), description: intl.formatMessage({ id: 'succession.value_acceleration.category_page.tour_your_gaps_desc' }), targetSelector: '[data-tutorial-id="your-gaps"]', position: 'right' },
    { id: 'action-plan', title: intl.formatMessage({ id: 'succession.value_acceleration.category_page.tour_action_plan_title' }), description: intl.formatMessage({ id: 'succession.value_acceleration.category_page.tour_action_plan_desc' }), targetSelector: '[data-tutorial-id="action-plan"]', position: 'right' },
    { id: 'whats-next', title: intl.formatMessage({ id: 'succession.value_acceleration.category_page.tour_whats_next_title' }), description: intl.formatMessage({ id: 'succession.value_acceleration.category_page.tour_whats_next_desc' }), targetSelector: '[data-tutorial-id="whats-next"]', position: 'top' },
  ];

  // Per-user localStorage key for tour gate
  const userId = user?.id || props?.current_user?.id || 'anonymous';
  const tourStorageKey = `value_acceleration_tour_shown_v1_${userId}`;

  useEffect(() => {
    // Don't run tour logic if in empty state
    if (!hasCompletedPlanBuilder && !urlBypassCheck) return;

    try {
      const params = new URLSearchParams(window.location.search || '');
      const tourParam = params.get('tour');
      const fromWizard = params.get('postWizardTour') === '1';

      // Handle URL parameter overrides
      if (tourParam === 'reset') {
        // Reset tour shown status and open
        localStorage.removeItem(tourStorageKey);
        setTourSteps(VALUE_ACCELERATION_TOUR_STEPS);
        setTimeout(() => setTourOpen(true), 300);
        return;
      }

      if (tourParam === 'open' || fromWizard) {
        // Force open tour (legacy postWizardTour support + new tour=open)
        setTourSteps(VALUE_ACCELERATION_TOUR_STEPS);
        setTimeout(() => setTourOpen(true), 300);
        // Mark as shown
        try { localStorage.setItem(tourStorageKey, 'true'); } catch (_) {}
        return;
      }

      // Auto-show on first visit when not in empty state
      const hasSeenTour = localStorage.getItem(tourStorageKey) === 'true';
      if (!hasSeenTour) {
        setTourSteps(VALUE_ACCELERATION_TOUR_STEPS);
        // Slight delay to allow page content to render
        setTimeout(() => setTourOpen(true), 500);
        // Mark as shown
        try { localStorage.setItem(tourStorageKey, 'true'); } catch (_) {}
      }
    } catch (_) {}
  }, [hasCompletedPlanBuilder, urlBypassCheck, tourStorageKey]);

  // Debug: Log what we're checking
  console.log('[ValueAccelerationCategoryPage] Render check - hasCompletedPlanBuilder:', hasCompletedPlanBuilder, 'urlBypassCheck:', urlBypassCheck);

  // Get plan progress for empty state (to show different button text if in progress)
  const planProgress = user.plan_builder_progress || user.user_plan_progress;

  // Empty state - show when PlanBuilder not completed AND no URL bypass
  if (!hasCompletedPlanBuilder && !urlBypassCheck) {
    return (
      <>
        <SingleColumnTemplate title={intl.formatMessage({ id: 'succession.value_acceleration.category_page.title' })} variant="wide">
          <PlanBuilderEmptyState planProgress={planProgress} />
        </SingleColumnTemplate>

        <SnackbarProvider maxSnack={3}>
          <SuccessionThemeProvider company={props.company} user={props.current_user}>
            <SuccessionDrawerWithListener />
          </SuccessionThemeProvider>
        </SnackbarProvider>
      </>
    );
  }

  // Empty state - show when assessment not completed
  if (!hasCompletedAssessment) {
    return (
      <>
        <SingleColumnTemplate title={intl.formatMessage({ id: 'succession.value_acceleration.category_page.title' })} variant="wide">
          <div className="min-h-[calc(100vh-200px)] flex items-center justify-center p-8">
            <div className="max-w-3xl mx-auto text-center space-y-8">
              {/* Icon/Visual */}
              <div className="flex justify-center">
                <div className="relative">
                  <div className="w-32 h-32 bg-gradient-to-br from-succession-primary/10 to-succession-secondary/10 rounded-full flex items-center justify-center">
                    <Settings className="w-16 h-16 text-succession-primary" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-12 h-12 bg-gradient-to-br from-succession-success/20 to-succession-success/10 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-succession-success" />
                  </div>
                </div>
              </div>

              {/* Headline */}
              <div className="space-y-4">
                <h1 className="text-4xl md:text-5xl font-bold text-succession-text-primary leading-tight">
                  {intl.formatMessage({ id: 'succession.value_acceleration.category_page.discover_path_headline' })}
                  <br />
                  <span className="text-succession-primary">
                    {intl.formatMessage({ id: 'succession.value_acceleration.category_page.discover_path_headline_highlight' })}
                  </span>
                </h1>
                <p className="text-lg text-succession-text-secondary max-w-2xl mx-auto leading-relaxed">
                  {intl.formatMessage({ id: 'succession.value_acceleration.category_page.discover_path_body' })}
                </p>
              </div>

              {/* Benefits */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl mx-auto">
                <div className="bg-succession-bg-primary rounded-xl p-6 border border-succession-border">
                  <div className="w-12 h-12 bg-succession-primary/10 rounded-lg flex items-center justify-center mb-4 mx-auto">
                    <CheckCircle2 className="w-6 h-6 text-succession-primary" />
                  </div>
                  <h3 className="font-semibold text-succession-text-primary mb-2">
                    {intl.formatMessage({ id: 'succession.value_acceleration.category_page.know_your_score_title' })}
                  </h3>
                  <p className="text-sm text-succession-text-secondary">
                    {intl.formatMessage({ id: 'succession.value_acceleration.category_page.know_your_score_detail' })}
                  </p>
                </div>
                <div className="bg-succession-bg-primary rounded-xl p-6 border border-succession-border">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                    <TrendingDown className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-succession-text-primary mb-2">
                    {intl.formatMessage({ id: 'succession.value_acceleration.category_page.identify_gaps_title' })}
                  </h3>
                  <p className="text-sm text-succession-text-secondary">
                    {intl.formatMessage({ id: 'succession.value_acceleration.category_page.identify_gaps_detail' })}
                  </p>
                </div>
                <div className="bg-succession-bg-primary rounded-xl p-6 border border-succession-border">
                  <div className="w-12 h-12 bg-succession-success/20 rounded-lg flex items-center justify-center mb-4 mx-auto">
                    <ArrowRight className="w-6 h-6 text-succession-success" />
                  </div>
                  <h3 className="font-semibold text-succession-text-primary mb-2">
                    {intl.formatMessage({ id: 'succession.value_acceleration.category_page.take_action_title' })}
                  </h3>
                  <p className="text-sm text-succession-text-secondary">
                    {intl.formatMessage({ id: 'succession.value_acceleration.category_page.take_action_detail' })}
                  </p>
                </div>
              </div>

              {/* CTA */}
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={() => {
                    try {
                      if (window.ahoy?.track) window.ahoy.track('succession_readiness_start_assessment_clicked', { source: 'value_acceleration_empty_state' });
                    } catch(_) {}
                    try {
                      const e = new CustomEvent('open-unified-intake');
                      window.dispatchEvent(e);
                    } catch(_) {}
                  }}
                  className="inline-flex items-center gap-3 px-10 py-5 bg-succession-primary hover:bg-succession-primary-hover text-white rounded-xl font-semibold transition-all text-lg shadow-succession-lg hover:shadow-succession-xl transform hover:scale-105"
                >
                  <Settings className="w-6 h-6" />
                  {intl.formatMessage({ id: 'succession.value_acceleration.category_page.start_assessment' })}
                  <ArrowRight className="w-5 h-5" />
                </button>
                <p className="text-sm text-succession-text-secondary">
                  {intl.formatMessage({ id: 'succession.value_acceleration.category_page.assessment_privacy_note' })}
                </p>
              </div>
            </div>
          </div>
        </SingleColumnTemplate>

        <SnackbarProvider maxSnack={3}>
          <SuccessionThemeProvider company={props.company} user={props.current_user}>
            <SuccessionDrawerWithListener />
          </SuccessionThemeProvider>
        </SnackbarProvider>
      </>
    );
  }

  return (
    <>
      <SingleColumnTemplate title={intl.formatMessage({ id: 'succession.value_acceleration.category_page.title' })} variant="wide">
        {/* Row 1: Readiness Score Hero */}
        <div className="mb-4" data-tutorial-id="readiness-score">
          <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>{intl.formatMessage({ id: 'succession.value_acceleration.category_page.your_readiness_score' })}</h3>
          <div className="mt-2">
            <ReadinessScoreHero />
          </div>
        </div>

        {/* Row 2: Your Gaps (Unified 4-card view) */}
        <div className="mb-4" data-tutorial-id="your-gaps">
          <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>{intl.formatMessage({ id: 'succession.value_acceleration.category_page.your_gaps' })}</h3>
          <div className="mt-2">
            <YourGapsCompact />
          </div>
        </div>

        {/* Row 3: Sprint / Action Plan */}
        <div className="mb-4" data-tutorial-id="action-plan">
          <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>{intl.formatMessage({ id: 'succession.value_acceleration.category_page.your_action_plan' })}</h3>
          <div className="mt-2">
            <ValueAccelerationSprintContainer />
          </div>
        </div>

        {/* Row 4: Decision Card + Hero Banner - Equal Heights */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:items-stretch">
          <div className="h-full flex flex-col" data-tutorial-id="whats-next">
            {shouldShowDecision ? (
              <GrowthExitDecisionCard
                user={user}
                onCompleted={() => {
                  try { if (window?.ahoy?.track) window.ahoy.track('GrowthExitPrompt', { action: 'hide_after_selection' }); } catch(_) {}
                  setHideDecision(true);
                }}
              />
            ) : (
              <ToolsGridTemplate title={intl.formatMessage({ id: 'succession.value_acceleration.category_page.popular_tools' })} tools={tools} className="h-full" />
            )}
          </div>
          <div className="h-full flex flex-col">
            {hasMarketplaceSprints ? (
              <HeroBannerTemplate
                title={intl.formatMessage({ id: 'succession.value_acceleration.category_page.diy_not_for_you_title' })}
                blurb={intl.formatMessage({ id: 'succession.value_acceleration.category_page.diy_not_for_you_blurb' })}
                ctaHref="/sprints/marketplace"
                ctaLabel={intl.formatMessage({ id: 'succession.value_acceleration.category_page.diy_cta_label' })}
                variant="brand"
                icon={Zap}
              />
            ) : (
              <HeroBannerTemplate
                title={intl.formatMessage({ id: 'succession.value_acceleration.category_page.maximize_value_title' })}
                blurb={intl.formatMessage({ id: 'succession.value_acceleration.category_page.maximize_value_blurb' })}
                ctaHref="/succession/education?category=Value%20Acceleration"
                ctaLabel={intl.formatMessage({ id: 'succession.value_acceleration.category_page.maximize_value_cta' })}
                variant="brand"
                icon={Zap}
              />
            )}
          </div>
        </div>
      </SingleColumnTemplate>

      <SnackbarProvider maxSnack={3}>
        <SuccessionThemeProvider company={props.company} user={props.current_user}>
          <SuccessionDrawerWithListener />
        </SuccessionThemeProvider>
      </SnackbarProvider>

      <GuidedTour
        isOpen={tourOpen}
        steps={tourSteps || []}
        onClose={() => setTourOpen(false)}
        onComplete={() => {
          setTourOpen(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        onReplayRequest={() => setTourOpen(true)}
      />
    </>
  );
};

export default ValueAccelerationCategoryPage;


